import React from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { INTAKE_URL } from "@/constants/links";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/z2fv8nq3_KvTYTB0tvuhMuJwoFUMzy.png";

export default function Hero() {
  return (
    <section
      data-testid={HERO.root}
      className="relative overflow-hidden border-b border-[color:var(--iw-border)] bg-white"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-60" aria-hidden />
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[640px] w-[640px] -translate-x-1/2 rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(37, 99, 235, 0.45), transparent 70%)" }}
        aria-hidden
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-12 lg:gap-12 lg:py-32">
        <div className="lg:col-span-7">
          <div className="iw-animate-up">
            {/* Prominent logo + brand mark */}
            <div className="mb-8 flex items-center gap-5">
              <img
                src="https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/amgkh1mp_logo.png"
                alt="Integrated Wellness logo"
                className="h-40 w-40 rounded-full object-cover ring-2 ring-[color:var(--iw-blue)]/30 shadow-[0_16px_42px_-12px_rgba(17,41,107,0.6)] sm:h-52 sm:w-52 lg:h-64 lg:w-64"
                decoding="async"
                loading="eager"
                width="256"
                height="256"
              />
              <div className="leading-tight">
                <div className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl">
                  Integrated Wellness
                </div>
                <div className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-[2.4rem]">
                  <span className="text-[color:var(--iw-accent-red)]">Optimize</span>{" "}
                  <span className="text-[color:var(--iw-primary)]">Yourself</span>
                  <span className="text-[color:var(--iw-accent-red)]">.</span>
                </div>
              </div>
            </div>

            <h1 className="font-display text-3xl font-bold leading-[1.1] tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl xl:text-[3.4rem]">
              Personalized{" "}
              <span className="text-[color:var(--iw-blue)]">Optimization Programs,</span>{" "}
              <span className="text-[color:var(--iw-primary)]">Powered by</span>{" "}
              <span className="text-[color:var(--iw-accent-red)]">Your Data</span>.
            </h1>

            <p className="mt-8 max-w-xl text-xl leading-relaxed text-[color:var(--iw-body)]">
              <strong className="font-semibold text-[color:var(--iw-primary)]">Medically supervised. Data-driven.</strong>{" "}
              The most advanced optimization program available — built on{" "}
              <strong className="font-semibold text-[color:var(--iw-primary)]">cutting-edge AI</strong> and{" "}
              <strong className="font-semibold text-[color:var(--iw-accent-red)]">7 patent-pending technologies</strong>.
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={INTAKE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HERO.primaryCta}
                className="iw-btn-red inline-flex items-center justify-center gap-2 px-7 py-4 font-mono text-xs uppercase tracking-[0.22em]"
              >
                Get Started <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="#tracks"
                data-testid={HERO.secondaryCta}
                className="iw-btn-ghost inline-flex items-center justify-center gap-2 px-7 py-4 font-mono text-xs uppercase tracking-[0.22em]"
              >
                See the Programs <ArrowDownRight className="h-4 w-4" />
              </a>
            </div>

          </div>
        </div>

        <div className="relative lg:col-span-5">
          <div className="iw-scan-sweep relative h-[460px] w-full overflow-hidden border border-[color:var(--iw-border)] sm:h-[560px] lg:h-[680px]">
            <img
              src={HERO_IMG}
              alt="Integrated Wellness — see your body, your mind, and your life as one connected system"
              className="h-full w-full object-cover"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/30 via-transparent to-transparent" />
            <div className="absolute left-0 top-0 w-full p-5">
              <div className="flex items-center justify-between border border-[color:var(--iw-blue)]/60 bg-white/10 px-4 py-2.5 font-mono text-[12px] uppercase tracking-[0.22em] text-white backdrop-blur sm:text-[13px]">
                <span>Body Scan · Live</span>
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[color:var(--iw-accent-red)]" />
                  Capturing
                </span>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 w-full p-5">
              <div className="border-l-2 border-[color:var(--iw-accent-red)] bg-white/12 px-4 py-3.5 backdrop-blur">
                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-white/95 sm:text-[13px]">
                  Your body. Your data. Your plan.
                </div>
                <div className="font-display text-xl font-semibold text-white sm:text-2xl">
                  All in one place.
                </div>
              </div>
            </div>
            <span className="pointer-events-none absolute left-3 top-3 h-3 w-3 border-l-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
            <span className="pointer-events-none absolute right-3 top-3 h-3 w-3 border-r-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
            <span className="pointer-events-none absolute left-3 bottom-3 h-3 w-3 border-l-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
            <span className="pointer-events-none absolute right-3 bottom-3 h-3 w-3 border-r-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
          </div>
          <div className="absolute -bottom-6 -right-2 hidden border border-[color:var(--iw-border)] bg-white px-4 py-3 font-mono text-[12px] uppercase tracking-[0.2em] text-[color:var(--iw-primary)] sm:block">
            <span className="text-[color:var(--iw-blue)]">●</span> 7 patents pending
          </div>
        </div>
      </div>

    </section>
  );
}
